import { ApolloClient, ApolloQueryResult, NormalizedCacheObject } from '@apollo/client';
import { PageBySlug, PageBySlugPreview } from './contentful/PageBySlug.graphql';
import { NewsPageBySlug } from './contentful/NewsPageBySlug.graphql';
import { TwoForOnePageBySlug } from './contentful/TwoForOnePageBySlug.graphql';
import { Footer } from './contentful/Footer.graphql';
import { Navbar } from './contentful/Navbar.graphql';
import { NewsEntries } from './contentful/NewsEntries.graphql';
import { PageCollectionWithSlugsAndIds } from './contentful/PageCollectionWithSlugsAndIds.graphql';
import { NewsPageCollectionWithSlugsAndIds } from './contentful/NewsPageCollectionWithSlugsAndIds.graphql';
import { PageSectionsBySystemId, PageSectionsBySystemIdPreview } from './contentful/PageSectionsBySystemId.graphql';
import { ProductCardsExtras } from './contentful/ProductCardsExtras.graphql';
import { BentoBoxById } from './contentful/BentoBoxById.graphql';
import { ContactFormById } from './contentful/ContactFormById.graphql';

import {
  BentoBoxByIdQuery,
  ContactFormByIdQuery,
  FooterQuery,
  NavbarQuery,
  NewsEntriesQuery,
  NewsPageBySlugQuery,
  NewsPageBySlugQueryVariables,
  NewsPageCollectionWithSlugsAndIdsQuery,
  NewsPageCollectionWithSlugsAndIdsQueryVariables,
  PageBySlugQuery,
  PageBySlugQueryVariables,
  PageCollectionWithSlugsAndIdsQuery,
  PageCollectionWithSlugsAndIdsQueryVariables,
  PageSectionsBySystemIdQuery,
  PageSectionsBySystemIdQueryVariables,
  ProductCardsExtrasQuery,
  TwoForOnePageBySlugQuery,
  TwoForOnePageBySlugQueryVariables,
} from 'types/generated';

export const querySectionsBySystemIds = async (
  client: ApolloClient<NormalizedCacheObject>,
  ids: string[],
  preview: boolean
): Promise<ApolloQueryResult<PageSectionsBySystemIdQuery>> =>
  await client.query<PageSectionsBySystemIdQuery, PageSectionsBySystemIdQueryVariables>({
    query: preview ? PageSectionsBySystemIdPreview : PageSectionsBySystemId,
    variables: {
      section_ids: ids,
      limit: ids.length,
    },
  });

export const queryPageCollectionWithSlugsAndIds = async (
  client: ApolloClient<NormalizedCacheObject>,
  skip = 0,
  limit = 50
): Promise<ApolloQueryResult<PageCollectionWithSlugsAndIdsQuery>> =>
  await client.query<PageCollectionWithSlugsAndIdsQuery, PageCollectionWithSlugsAndIdsQueryVariables>({
    query: PageCollectionWithSlugsAndIds,
    variables: {
      skip,
      limit,
    },
  });

export const queryNewsPageCollectionWithSlugsAndIds = async (
  client: ApolloClient<NormalizedCacheObject>,
  skip = 0,
  limit = 50
): Promise<ApolloQueryResult<NewsPageCollectionWithSlugsAndIdsQuery>> =>
  await client.query<NewsPageCollectionWithSlugsAndIdsQuery, NewsPageCollectionWithSlugsAndIdsQueryVariables>({
    query: NewsPageCollectionWithSlugsAndIds,
    variables: {
      skip,
      limit,
    },
  });

export const queryPageBySlug = async (
  client: ApolloClient<NormalizedCacheObject>,
  slug: string,
  preview: boolean
): Promise<ApolloQueryResult<PageBySlugQuery>> =>
  await client.query<PageBySlugQuery, PageBySlugQueryVariables>({
    query: preview ? PageBySlugPreview : PageBySlug,
    variables: {
      slug,
    },
  });

export const queryNewsPageBySlug = async (
  client: ApolloClient<NormalizedCacheObject>,
  slug: string
): Promise<ApolloQueryResult<NewsPageBySlugQuery>> =>
  await client.query<NewsPageBySlugQuery, NewsPageBySlugQueryVariables>({
    query: NewsPageBySlug,
    variables: {
      slug,
    },
  });

export const queryFooter = async (
  client: ApolloClient<NormalizedCacheObject>
): Promise<ApolloQueryResult<FooterQuery>> =>
  await client.query<FooterQuery>({
    query: Footer,
  });

export const queryNavbar = async (
  client: ApolloClient<NormalizedCacheObject>,
  uniqKey = 'Header navigation' // default navbar
): Promise<ApolloQueryResult<NavbarQuery>> =>
  client.query<NavbarQuery>({
    query: Navbar,
    variables: { uniqKey },
  });

export const queryNewsEntries = async (
  client: ApolloClient<NormalizedCacheObject>,
  skip: number,
  limit: number
): Promise<ApolloQueryResult<NewsEntriesQuery>> =>
  await client.query<NewsEntriesQuery>({
    query: NewsEntries,
    variables: {
      skip,
      limit,
    },
  });

export const queryTwoForOnePageBySlug = async (
  client: ApolloClient<NormalizedCacheObject>,
  slug: string
): Promise<ApolloQueryResult<TwoForOnePageBySlugQuery>> => {
  // Execute the query as before
  return await client.query<TwoForOnePageBySlugQuery, TwoForOnePageBySlugQueryVariables>({
    query: TwoForOnePageBySlug,
    variables: {
      slug,
    },
    errorPolicy: 'all',
  });
};

export const queryBentoBoxById = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: string
): Promise<ApolloQueryResult<BentoBoxByIdQuery>> =>
  await client.query<BentoBoxByIdQuery>({
    query: BentoBoxById,
    variables: { id: id },
  });

export const queryContactFormById = async (
  client: ApolloClient<NormalizedCacheObject>,
  id: string
): Promise<ApolloQueryResult<ContactFormByIdQuery>> =>
  await client.query<ContactFormByIdQuery>({
    query: ContactFormById,
    variables: { id: id },
  });

export const queryProductCardsExtras = async (
  client: ApolloClient<NormalizedCacheObject>,
  ids: Array<string>,
  limitItems: number,
  limitImages: number
): Promise<ApolloQueryResult<ProductCardsExtrasQuery>> =>
  await client.query<ProductCardsExtrasQuery>({
    query: ProductCardsExtras,
    variables: { ids: ids, limitItems: limitItems, limitImages: limitImages },
  });
